import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  const featuredReviews = [
  {
    id: 1,
    title: "Dyson V15 Detect",
    rating: 4.8,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e4d98958-1768061388011.png",
    alt: "Modern cordless vacuum cleaner with digital display on hardwood floor",
    badge: "Editor\'s Choice",
    summary: "Revolutionary laser dust detection technology meets powerful suction in this premium cordless vacuum.",
    testScore: 95,
    priceRange: "$749 - $799"
  },
  {
    id: 2,
    title: "Shark Navigator Lift-Away",
    rating: 4.6,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4b4b0ce-1766875571363.png",
    alt: "Blue upright vacuum cleaner with detachable canister on carpet",
    badge: "Best Value",
    summary: "Versatile 2-in-1 design delivers professional cleaning power at an affordable price point.",
    testScore: 88,
    priceRange: "$179 - $229"
  },
  {
    id: 3,
    title: "Bissell CrossWave Pet Pro",
    rating: 4.4,
    image: "https://images.unsplash.com/photo-1699412025698-15a3426138df",
    alt: "Multi-surface cleaning machine with water tank on kitchen tile floor",
    badge: "Pet Owner\'s Pick",
    summary: "Simultaneous vacuum and wash technology perfect for homes with furry family members.",
    testScore: 82,
    priceRange: "$249 - $299"
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewIndex((prev) => (prev + 1) % featuredReviews?.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [featuredReviews?.length]);

  const currentReview = featuredReviews?.[currentReviewIndex];

  return (
    <section className="relative bg-gradient-to-br from-primary via-primary to-blue-900 text-white overflow-hidden">
      <div className="absolute inset-0 bg-black/10"></div>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 border border-white/20 rounded-full"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 border border-white/20 rounded-full"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Side */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={24} className="text-accent" />
                <span className="text-accent font-semibold text-sm uppercase tracking-wide">
                  Trusted by 2M+ Homeowners
                </span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Find Your Perfect
                <span className="text-accent block">Vacuum Cleaner</span>
              </h1>
              
              <p className="text-xl text-blue-100 leading-relaxed max-w-lg">
                Expert reviews, honest comparisons, and personalized recommendations to help you make the right choice for your home.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="Zap"
                iconPosition="left"
                className="bg-accent hover:bg-accent/90 text-gray-900 font-semibold"
                asChild>

                <Link to="/buying-guide">Start Buying Guide</Link>
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                className="border-white/30 text-white hover:bg-white/10">

                Watch How We Test
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={20} className="text-green-400" />
                <span className="text-sm text-blue-100">FTC Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={20} className="text-green-400" />
                <span className="text-sm text-blue-100">Expert Tested</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={20} className="text-green-400" />
                <span className="text-sm text-blue-100">500+ Reviews</span>
              </div>
            </div>
          </div>

          {/* Featured Review Card */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-6 text-gray-900 transform hover:scale-105 transition-transform duration-300">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-accent text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                  {currentReview?.badge}
                </span>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={16} className="text-yellow-500 fill-current" />
                  <span className="font-semibold">{currentReview?.rating}</span>
                </div>
              </div>

              <div className="aspect-video mb-4 rounded-lg overflow-hidden">
                <Image
                  src={currentReview?.image}
                  alt={currentReview?.alt}
                  className="w-full h-full object-cover" />

              </div>

              <h3 className="text-xl font-bold mb-2">{currentReview?.title}</h3>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                {currentReview?.summary}
              </p>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{currentReview?.testScore}</div>
                    <div className="text-xs text-gray-500">Test Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">{currentReview?.priceRange}</div>
                    <div className="text-xs text-gray-500">Price Range</div>
                  </div>
                </div>
              </div>

              <Button
                variant="default"
                size="sm"
                iconName="ArrowRight"
                iconPosition="right"
                fullWidth
                asChild>

                <Link to="/product-review">Read Full Review</Link>
              </Button>
            </div>

            {/* Review Navigation Dots */}
            <div className="flex justify-center space-x-2 mt-6">
              {featuredReviews?.map((_, index) =>
              <button
                key={index}
                onClick={() => setCurrentReviewIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentReviewIndex ?
                'bg-accent scale-125' : 'bg-white/30 hover:bg-white/50'}`
                } />

              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;